import React, { useEffect, useMemo, useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import MultiSelect from "../../../component-lib/Atoms/MultiSelect/MultiSelect";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import Button from "../../../component-lib/Molecules/button/Button";
import Input from "../../../component-lib/Atoms/Input/Input";
import EventEHRCheckbox from "../../../component-lib/Molecules/checkbox/EventEHRCheckbox";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import {
  RULE_DATA,
  CHANNEL_OPTIONS,
  DISPOSITION_OPTIONS,
  TRANSPORT_LEVEL_OPTIONS,
} from "../communications-screens/constants";
import FacilityService from "../../../services/FacilityService";
import ContactDirectoryService from "../../../services/ContactDirectoryService";
import TemplateService from "../../../services/TemplateService";
import {
  getLazyLoadingApiResponse,
  getLazyLoadingSearchApiResponse,
} from "../../../utils/constantsFunctions";
import EventEHRMultiSelectWithSearch from "../../../component-lib/Atoms/Select/EventEHRMultiSelectWithSearch";
import {
  createCommunicationRule,
  updateCommunicationRule,
} from "../SettingsSaga";
import { setCloseAddCommunicationRuleDialog } from "../SettingsSlice";

type Option = { label: string; value: string };

interface AddRuleDialogProps {
  readonly isOpen: boolean;
  readonly handleData: (data: Record<string, unknown>) => void;
  readonly handleClose: () => void;
  readonly isEdit?: boolean;
  readonly defaultRuleData?: Record<string, unknown>;
}

// End date defaults to 1 Aug 2026 per requirement.
const DEFAULT_END_DATE = "2026-08-01";

export default function AddRuleDialog({
  isOpen = false,
  handleClose,
  handleData,
  isEdit = false,
  defaultRuleData = {},
}: AddRuleDialogProps) {
  const dialogHeading = isEdit ? "Edit Rule" : "Add Rule";
  const dispatch = useDispatch();

  const closeAddCommunicationRuleDialog = useSelector(
    (state: any) => state?.SETTINGS_SLICE?.closeAddCommunicationRuleDialog,
  );

  // Selections
  const [disposition, setDisposition] = useState<string>("");
  // Transport level — shown only when disposition is Transfer Off Property.
  const [transportLevel, setTransportLevel] = useState<string>("");
  const [facilityIds, setFacilityIds] = useState<string[]>([]);
  // Selected directory contacts as {label,value} objects (Autocomplete value).
  const [contactRecipients, setContactRecipients] = useState<Option[]>([]);
  // Selected user recipients as {label,value} objects (Autocomplete value).
  const [userRecipients, setUserRecipients] = useState<Option[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  // Option lists
  const [facilityOptions, setFacilityOptions] = useState<Option[]>([]);

  // ---- Lazy-loaded, searchable Directory Contacts picker ----
  const [contactDropdownOptions, setContactDropdownOptions] = useState<any[]>(
    [],
  );
  const [contactPageCount, setContactPageCount] = useState(1);
  const [contactSearch, setContactSearch] = useState("");
  const [contactResetFlag, setContactResetFlag] = useState(false);
  const [contactTimer, setContactTimer] = useState<NodeJS.Timeout | null>(null);
  const contactInitialLoad = React.useRef(false);

  // ---- Lazy-loaded, searchable Provider/Staff/Admin picker ----
  const [userDropdownOptions, setUserDropdownOptions] = useState<any[]>([]);
  const [userPageCount, setUserPageCount] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userResetFlag, setUserResetFlag] = useState(false);
  const [userTimer, setUserTimer] = useState<NodeJS.Timeout | null>(null);
  const userInitialLoad = React.useRef(false);
  // id -> "admin" | "provider", so the payload can pick adminUserId vs userId
  // even for users no longer present in the current (filtered) option page.
  const userSourceById = React.useRef<Record<string, string>>({});

  const [emailTemplates, setEmailTemplates] = useState<Option[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<Option[]>([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState<
    string | null
  >(null);
  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState<
    string | null
  >(null);
  const [selectedEmailTemplateLabel, setSelectedEmailTemplateLabel] = useState<
    string | null
  >(null);
  const [selectedSmsTemplateLabel, setSelectedSmsTemplateLabel] = useState<
    string | null
  >(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Stable initial dates so Formik's enableReinitialize doesn't reset the form
  // (a fresh moment() each render would look like changed initial values and
  // wipe user input such as the Rule Name).
  const initialStartDate = useMemo(
    () =>
      typeof defaultRuleData?.startDate === "string"
        ? moment(defaultRuleData.startDate)
        : moment(),
    [defaultRuleData?.startDate],
  );
  const initialEndDate = useMemo(
    () =>
      typeof defaultRuleData?.endDate === "string"
        ? moment(defaultRuleData.endDate)
        : moment(DEFAULT_END_DATE),
    [defaultRuleData?.endDate],
  );

  const validationSchema = Yup.object().shape({
    ruleName: Yup.string().trim().required("Rule name is required"),
    [RULE_DATA.START_DATE]: Yup.date()
      .required("Start Date is required")
      .test("not-past", "Start date cannot be in the past", function (value) {
        if (!value) return true;
        return moment(value).isSameOrAfter(moment(), "day");
      }),
    [RULE_DATA.END_DATE]: Yup.date()
      .required("End Date is required")
      .test(
        "is-after-start",
        "End date must be greater than start date",
        function (value) {
          const startDate = this.parent[RULE_DATA.START_DATE];
          if (!startDate || !value) return true;
          return moment(value).isAfter(moment(startDate), "day");
        },
      ),
  });

  // Custom validation for the non-Formik fields; returns the list of errors.
  const validateForm = (): string[] => {
    const errors: string[] = [];
    if (!disposition) errors.push("Disposition trigger is required");
    if (contactRecipients.length === 0 && userRecipients.length === 0)
      errors.push("At least one recipient is required");
    if (selectedChannels.length === 0)
      errors.push("At least one channel is required");
    if (selectedChannels.includes("email") && !selectedEmailTemplateId)
      errors.push("Email template is required when Email channel is selected");
    if (selectedChannels.includes("sms") && !selectedSmsTemplateId)
      errors.push("SMS template is required when SMS channel is selected");
    return errors;
  };

  // ---- Data loading ----
  const fetchFacilities = async () => {
    try {
      const res = await FacilityService.getAllFacilities();
      if (res?.success) setFacilityOptions(res?.facilities || []);
    } catch (e) {
      /* keep empty */
    }
  };

  const mapContacts = (contacts: any[]): Option[] =>
    contacts.map((c: any) => ({
      label: c?.contactNumber
        ? `${c?.name} (${c?.contactNumber})`
        : `${c?.name}`,
      value: String(c?.id),
    }));

  // Paged fetch (scroll-to-bottom appends the next page).
  const getContactsList = async (page: number, reset = false) => {
    try {
      const res = await ContactDirectoryService.getContactDirectoryList({
        pageNumber: page,
        limit: 10,
      });
      const mapped = mapContacts(res?.data?.contact || []);
      if (mapped.length > 0) {
        if (reset) {
          setContactDropdownOptions(mapped);
          setContactResetFlag(false);
        } else {
          setContactDropdownOptions((prev) => prev.concat(mapped));
        }
      } else if (reset) {
        setContactDropdownOptions([]);
        setContactResetFlag(false);
      }
    } catch (e) {
      /* keep empty */
    }
  };

  // Server-side search (backend search key is `searchKey`).
  const getContactsSearchList = async (search: string) => {
    try {
      const res = await ContactDirectoryService.getContactDirectoryList({
        pageNumber: 1,
        limit: 10,
        searchKey: search,
      });
      setContactDropdownOptions(mapContacts(res?.data?.contact || []));
    } catch (e) {
      /* keep empty */
    }
  };

  // Debounced search-input handler.
  const handleContactSearchChange = (value: string) => {
    if (contactTimer) clearTimeout(contactTimer);
    const t = setTimeout(() => setContactSearch(value), 500);
    setContactTimer(t);
  };

  // Merge a fetched page's admin-vs-provider source into the ref map.
  const mergeUserSources = (users: any[]) => {
    users.forEach((u: any) => {
      userSourceById.current[String(u?.id)] =
        u?.source === "admin" ? "admin" : "provider";
    });
  };

  const mapUsers = (users: any[]): Option[] =>
    users.map((u: any) => ({
      label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "-",
      value: String(u?.id),
    }));

  // Paged fetch (scroll-to-bottom appends the next page).
  const getUsersList = async (page: number, reset = false) => {
    try {
      const res = await getLazyLoadingApiResponse("user", page, 10, {
        isTaskAssignment: true,
      });
      const users = res?.data?.users || [];
      mergeUserSources(users);
      const mapped = mapUsers(users);
      if (mapped.length > 0) {
        if (reset) {
          setUserDropdownOptions(mapped);
          setUserResetFlag(false);
        } else {
          setUserDropdownOptions((prev) => prev.concat(mapped));
        }
      } else if (reset) {
        setUserDropdownOptions([]);
        setUserResetFlag(false);
      }
    } catch (e) {
      /* keep empty */
    }
  };

  // Server-side search (backend search key is `name`).
  const getUsersSearchList = async (search: string) => {
    try {
      const res = await getLazyLoadingSearchApiResponse(
        "user",
        search,
        "name",
        {
          isTaskAssignment: true,
        },
      );
      const users = res?.data?.users || [];
      mergeUserSources(users);
      setUserDropdownOptions(mapUsers(users));
    } catch (e) {
      /* keep empty */
    }
  };

  // Debounced search-input handler.
  const handleUserSearchChange = (value: string) => {
    if (userTimer) clearTimeout(userTimer);
    const t = setTimeout(() => setUserSearch(value), 500);
    setUserTimer(t);
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await TemplateService.getEmailTemplates({
        pageNumber: 1,
        limit: 100,
      });
      if (res?.success) setEmailTemplates(res?.templates || []);
    } catch (e) {
      setEmailTemplates([]);
    }
  };

  const fetchSmsTemplates = async () => {
    try {
      const res = await TemplateService.getSmsTemplates({
        pageNumber: 1,
        limit: 100,
      });
      if (res?.success) setSmsTemplates(res?.templates || []);
    } catch (e) {
      setSmsTemplates([]);
    }
  };

  // Load options + prepopulate on open
  useEffect(() => {
    if (!isOpen) return;
    fetchFacilities();
    // Initial page-1 load for the searchable Directory Contacts picker.
    contactInitialLoad.current = true;
    setContactPageCount(1);
    getContactsList(1, true);
    // Initial page-1 load for the searchable user picker.
    userInitialLoad.current = true;
    setUserPageCount(1);
    getUsersList(1, true);
    fetchEmailTemplates();
    fetchSmsTemplates();

    if (isEdit && defaultRuleData && Object.keys(defaultRuleData).length > 0) {
      const data: any = defaultRuleData;
      setDisposition(data?.dispositions?.[0] || "");
      setTransportLevel(data?.transportLevels?.[0] || "");
      setFacilityIds(
        data?.facilityIds ||
          (data?.facilities || []).map((f: any) => String(f?.id)) ||
          [],
      );

      const recipients: any[] = data?.recipients || [];
      const dirRecipients: Option[] = [];
      const usrRecipients: Option[] = [];
      let anyEmail = false;
      let anySms = false;
      recipients.forEach((r: any) => {
        if (r?.recipientType === "directory") {
          if (r?.contactDirectoryId) {
            const c = r?.contactDirectory;
            const label = c?.name
              ? c?.contactNumber
                ? `${c?.name} (${c?.contactNumber})`
                : `${c?.name}`
              : String(r.contactDirectoryId);
            dirRecipients.push({
              label,
              value: String(r.contactDirectoryId),
            });
          }
        } else {
          const id = r?.userId || r?.adminUserId;
          if (id) {
            const u = r?.user || r?.adminUser;
            const label =
              `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || String(id);
            usrRecipients.push({ label, value: String(id) });
            userSourceById.current[String(id)] = r?.adminUserId
              ? "admin"
              : "provider";
          }
          if (r?.isEmail) anyEmail = true;
          if (r?.isSMS) anySms = true;
        }
      });
      setContactRecipients(dirRecipients);
      setUserRecipients(usrRecipients);

      const channels: string[] = [];
      if (anyEmail || data?.isEmail || data?.emailTemplateId)
        channels.push("email");
      if (
        anySms ||
        data?.isSMS ||
        data?.smsTemplateId ||
        dirRecipients.length > 0
      )
        channels.push("sms");
      setSelectedChannels(channels);

      setSelectedEmailTemplateId(data?.emailTemplateId || null);
      setSelectedSmsTemplateId(data?.smsTemplateId || null);
      setSelectedEmailTemplateLabel(data?.emailTemplate?.name || null);
      setSelectedSmsTemplateLabel(data?.smsTemplate?.name || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset everything on close
  useEffect(() => {
    if (isOpen) return;
    setDisposition("");
    setTransportLevel("");
    setFacilityIds([]);
    setContactRecipients([]);
    setUserRecipients([]);
    setSelectedChannels([]);
    setFacilityOptions([]);
    setContactDropdownOptions([]);
    setContactPageCount(1);
    setContactSearch("");
    setContactResetFlag(false);
    contactInitialLoad.current = false;
    setUserDropdownOptions([]);
    setUserPageCount(1);
    setUserSearch("");
    setUserResetFlag(false);
    userInitialLoad.current = false;
    userSourceById.current = {};
    setEmailTemplates([]);
    setSmsTemplates([]);
    setSelectedEmailTemplateId(null);
    setSelectedSmsTemplateId(null);
    setSelectedEmailTemplateLabel(null);
    setSelectedSmsTemplateLabel(null);
    setSubmitError(null);
  }, [isOpen]);

  // Handle Redux-driven close after a successful create/update
  useEffect(() => {
    if (closeAddCommunicationRuleDialog) {
      handleData({ success: true });
      handleClose();
      dispatch(setCloseAddCommunicationRuleDialog(false));
    }
  }, [closeAddCommunicationRuleDialog, handleData, handleClose, dispatch]);

  // Search / next-page loader for the user picker (mirrors CreateGroupDialog).
  useEffect(() => {
    if (!isOpen || !userInitialLoad.current || userResetFlag) return;
    if (userSearch && userSearch.length > 0) {
      getUsersSearchList(userSearch);
    } else if (userPageCount > 1) {
      getUsersList(userPageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, userPageCount, userResetFlag, isOpen]);

  // Reset back to page 1 when the Autocomplete requests a reset (on close/clear).
  useEffect(() => {
    if (!isOpen || !userResetFlag) return;
    setUserSearch("");
    setUserPageCount(1);
    getUsersList(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResetFlag, isOpen]);

  // Search / next-page loader for the Directory Contacts picker.
  useEffect(() => {
    if (!isOpen || !contactInitialLoad.current || contactResetFlag) return;
    if (contactSearch && contactSearch.length > 0) {
      getContactsSearchList(contactSearch);
    } else if (contactPageCount > 1) {
      getContactsList(contactPageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactSearch, contactPageCount, contactResetFlag, isOpen]);

  useEffect(() => {
    if (!isOpen || !contactResetFlag) return;
    setContactSearch("");
    setContactPageCount(1);
    getContactsList(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactResetFlag, isOpen]);

  const buildPayload = (values: Record<string, any>) => {
    const isEmail = selectedChannels.includes("email");
    const isSMS = selectedChannels.includes("sms");

    const recipients: any[] = [
      ...contactRecipients.map((opt) => ({
        recipientType: "directory",
        contactDirectoryId: String(opt.value),
      })),
      ...userRecipients.map((opt) => {
        const id = String(opt.value);
        const isAdmin = userSourceById.current[id] === "admin";
        return {
          recipientType: "provider",
          ...(isAdmin ? { adminUserId: id } : { userId: id }),
        };
      }),
    ];

    return {
      name: values?.ruleName,
      dispositions: disposition ? [disposition] : [],
      transportLevels:
        disposition === "transferOffProperty" && transportLevel
          ? [transportLevel]
          : [],
      facilityIds,
      recipients,
      isEmail,
      isSMS,
      emailTemplateId: isEmail ? selectedEmailTemplateId : null,
      smsTemplateId: isSMS ? selectedSmsTemplateId : null,
      startDate: values?.[RULE_DATA.START_DATE]
        ? moment(values[RULE_DATA.START_DATE]).toISOString()
        : null,
      endDate: values?.[RULE_DATA.END_DATE]
        ? moment(values[RULE_DATA.END_DATE]).toISOString()
        : null,
    };
  };

  const multiValues = (e: any): string[] =>
    Array.isArray(e?.target?.value) ? e.target.value : [e?.target?.value];

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      customWidth="1120px"
    >
      <Formik
        initialValues={{
          ruleName: (defaultRuleData?.name as string) || "",
          [RULE_DATA.START_DATE]: initialStartDate,
          [RULE_DATA.END_DATE]: initialEndDate,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          setIsSubmitting(true);
          setSubmitError(null);

          const validationErrors = validateForm();
          if (validationErrors.length > 0) {
            setSubmitError(validationErrors.join(", "));
            setIsSubmitting(false);
            setSubmitting(false);
            return;
          }

          const payload = buildPayload(values);
          if (isEdit) {
            dispatch(
              updateCommunicationRule({
                ruleId: defaultRuleData?.id,
                payload,
              }),
            );
          } else {
            dispatch(createCommunicationRule(payload));
          }
          setIsSubmitting(false);
          setSubmitting(false);
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          setFieldTouched,
          handleBlur,
          handleSubmit,
        }) => {
          const validationErrorList = validateForm();
          const submitDisabled =
            !isValid ||
            isSubmitting ||
            validationErrorList.length > 0 ||
            (!isEdit && !dirty);

          // Ensure selected template ids always have an option so their label
          // renders in edit mode even if not in the fetched list.
          const emailTemplateOptions =
            selectedEmailTemplateId &&
            !emailTemplates.some((t) => t.value === selectedEmailTemplateId)
              ? [
                  {
                    label:
                      selectedEmailTemplateLabel || selectedEmailTemplateId,
                    value: selectedEmailTemplateId,
                  },
                  ...emailTemplates,
                ]
              : emailTemplates;
          const smsTemplateOptions =
            selectedSmsTemplateId &&
            !smsTemplates.some((t) => t.value === selectedSmsTemplateId)
              ? [
                  {
                    label: selectedSmsTemplateLabel || selectedSmsTemplateId,
                    value: selectedSmsTemplateId,
                  },
                  ...smsTemplates,
                ]
              : smsTemplates;

          // Keep selected recipient/facility ids visible even if not in the
          // first fetched page (edit mode).
          const withSelected = (
            opts: Option[],
            selected: string[],
          ): Option[] => {
            const missing = selected
              .filter((id) => !opts.some((o) => o.value === id))
              .map((id) => ({ label: id, value: id }));
            return [...missing, ...opts];
          };

          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />

              <DialogContent
                sx={{
                  padding: "16px 32px !important",
                  maxHeight: "70vh",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <div style={{ display: "grid", gap: "16px" }}>
                  {/* Rule Name */}
                  <div>
                    <div className="font-weight-500 font-size-14px color-565656 mb-3">
                      Rule Name
                    </div>
                    <Input
                      placeholder="Enter rule name"
                      type="text"
                      name="ruleName"
                      value={String(values?.ruleName || "")}
                      onChangeCb={(e) =>
                        setFieldValue("ruleName", e?.target?.value)
                      }
                      onBlur={handleBlur}
                      size="small"
                      error={touched.ruleName && !!errors.ruleName}
                      helperText={
                        touched.ruleName && errors.ruleName
                          ? String(errors.ruleName)
                          : undefined
                      }
                    />
                  </div>

                  {/* Disposition Trigger + Transport Level + Facilities */}
                  <div style={{ display: "flex", gap: "32px" }}>
                    <div style={{ flex: 1 }}>
                      <div className="font-weight-500 font-size-14px color-565656 mb-3">
                        Disposition Trigger
                      </div>
                      <SelectDropdown
                        name="disposition"
                        label="Select disposition"
                        placeholderValue="Select disposition"
                        displayEmpty={true}
                        value={disposition}
                        options={DISPOSITION_OPTIONS}
                        onChangeCb={(e) => {
                          setDisposition(e.target.value);
                          if (e.target.value !== "transferOffProperty") {
                            setTransportLevel("");
                          }
                        }}
                      />
                    </div>
                    {/* Transport Level — only for Transfer Off Property */}
                    {disposition === "transferOffProperty" && (
                      <div style={{ flex: 1 }}>
                        <div className="font-weight-500 font-size-14px color-565656 mb-3">
                          Transport Level
                        </div>
                        <SelectDropdown
                          name="transportLevel"
                          // label="Select transport level"
                          placeholderValue="Select transport level"
                          displayEmpty={true}
                          value={transportLevel}
                          options={TRANSPORT_LEVEL_OPTIONS}
                          onChangeCb={(e) => setTransportLevel(e.target.value)}
                        />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div className="font-weight-500 font-size-14px color-565656 mb-3">
                        Facilities (leave empty for all)
                      </div>
                      <MultiSelect
                        name="facilityIds"
                        label="All facilities"
                        placeholder="All facilities"
                        value={facilityIds}
                        options={withSelected(facilityOptions, facilityIds)}
                        onChangeCb={(e) => setFacilityIds(multiValues(e))}
                      />
                    </div>
                  </div>

                  {/* Recipients */}
                  <div>
                    <div className="font-weight-500 font-size-14px color-565656 mb-3">
                      Recipients
                    </div>
                    <div className="mb-6">
                      <div className="font-weight-500 font-size-12px color-565656 mb-2">
                        Directory Contacts
                      </div>
                      <EventEHRMultiSelectWithSearch
                        top100Films={contactDropdownOptions}
                        values={contactRecipients}
                        handleSearch={(e: any) =>
                          handleContactSearchChange(e?.target?.value)
                        }
                        handleChange={(_e: any, selected: any) =>
                          setContactRecipients(selected)
                        }
                        setPageCount={(v: any) => setContactPageCount(v)}
                        setResetFlag={(v: boolean) => setContactResetFlag(v)}
                      />
                    </div>
                    <div>
                      <div className="font-weight-500 font-size-12px color-565656 mb-2">
                        Provider / Staff / Admin
                      </div>
                      <EventEHRMultiSelectWithSearch
                        top100Films={userDropdownOptions}
                        values={userRecipients}
                        handleSearch={(e: any) =>
                          handleUserSearchChange(e?.target?.value)
                        }
                        handleChange={(_e: any, selected: any) =>
                          setUserRecipients(selected)
                        }
                        setPageCount={(v: any) => setUserPageCount(v)}
                        setResetFlag={(v: boolean) => setUserResetFlag(v)}
                      />
                    </div>
                    <div className="font-weight-400 font-size-12px color-565656 mt-3">
                      Note: Only recipients with a valid email and/or phone
                      number on file will receive the email and/or SMS.
                    </div>
                  </div>

                  {/* Channel */}
                  <div>
                    <div className="font-weight-500 font-size-14px color-565656 mb-3">
                      Channel
                    </div>
                    <EventEHRCheckbox
                      optionList={CHANNEL_OPTIONS}
                      style={{ display: "flex", gap: "24px" }}
                      initialValues={selectedChannels}
                      onChangeCb={(selectedValues: string[]) => {
                        if (!selectedValues.includes("email")) {
                          setSelectedEmailTemplateId(null);
                          setSelectedEmailTemplateLabel(null);
                        }
                        if (!selectedValues.includes("sms")) {
                          setSelectedSmsTemplateId(null);
                          setSelectedSmsTemplateLabel(null);
                        }
                        setSelectedChannels(selectedValues);
                      }}
                    />
                  </div>

                  {/* Templates */}
                  {(selectedChannels.includes("sms") ||
                    selectedChannels.includes("email")) && (
                    <div style={{ display: "flex", gap: "32px" }}>
                      {selectedChannels.includes("sms") && (
                        <div style={{ flex: 1 }}>
                          <div className="font-weight-500 font-size-14px color-565656 mb-3">
                            SMS Template
                          </div>
                          <SelectDropdown
                            name="smsTemplate"
                            label="Select SMS template"
                            value={selectedSmsTemplateId || ""}
                            options={smsTemplateOptions}
                            isStarPresent={true}
                            onChangeCb={(e) =>
                              setSelectedSmsTemplateId(e.target.value)
                            }
                            error={!selectedSmsTemplateId}
                            helperText={
                              !selectedSmsTemplateId
                                ? smsTemplates.length === 0
                                  ? "No SMS templates available"
                                  : "SMS template is required"
                                : undefined
                            }
                          />
                        </div>
                      )}
                      {selectedChannels.includes("email") && (
                        <div style={{ flex: 1 }}>
                          <div className="font-weight-500 font-size-14px color-565656 mb-3">
                            Email Template
                          </div>
                          <SelectDropdown
                            name="emailTemplate"
                            label="Select Email template"
                            value={selectedEmailTemplateId || ""}
                            options={emailTemplateOptions}
                            isStarPresent={true}
                            onChangeCb={(e) =>
                              setSelectedEmailTemplateId(e.target.value)
                            }
                            error={!selectedEmailTemplateId}
                            helperText={
                              !selectedEmailTemplateId
                                ? emailTemplates.length === 0
                                  ? "No email templates available"
                                  : "Email template is required"
                                : undefined
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rule Validity */}
                  <div>
                    <div className="font-weight-500 font-size-14px color-565656 mb-3">
                      Rule Validity
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <DatePicker
                          name={RULE_DATA.START_DATE}
                          label="Start Date"
                          value={values?.[RULE_DATA.START_DATE] || undefined}
                          onChangeCb={(date) => {
                            setFieldValue(RULE_DATA.START_DATE, date);
                            if (values?.[RULE_DATA.END_DATE]) {
                              setFieldTouched(RULE_DATA.END_DATE, true);
                            }
                          }}
                          isStarPresent={false}
                          minDate={moment()}
                          error={
                            touched[RULE_DATA.START_DATE] &&
                            !!errors[RULE_DATA.START_DATE]
                          }
                          helperText={
                            touched[RULE_DATA.START_DATE] &&
                            errors[RULE_DATA.START_DATE]
                              ? String(errors[RULE_DATA.START_DATE])
                              : undefined
                          }
                        />
                      </div>
                      <div>
                        <DatePicker
                          name={RULE_DATA.END_DATE}
                          label="End Date"
                          value={values?.[RULE_DATA.END_DATE] || undefined}
                          onChangeCb={(date) => {
                            setFieldValue(RULE_DATA.END_DATE, date);
                            setFieldTouched(RULE_DATA.END_DATE, true);
                          }}
                          isStarPresent={false}
                          minDate={values?.[RULE_DATA.START_DATE] || moment()}
                          error={
                            touched[RULE_DATA.END_DATE] &&
                            !!errors[RULE_DATA.END_DATE]
                          }
                          helperText={
                            touched[RULE_DATA.END_DATE] &&
                            errors[RULE_DATA.END_DATE]
                              ? String(errors[RULE_DATA.END_DATE])
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {submitError && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="text-red-800 text-sm">{submitError}</div>
                    </div>
                  )}
                </div>
              </DialogContent>

              <DialogActions style={{ padding: "24px 40px" }}>
                <Button
                  label={
                    isSubmitting
                      ? isEdit
                        ? "Updating..."
                        : "Creating..."
                      : TEXT_CONSTANTS?.SUBMIT
                  }
                  variant="contained"
                  type="submit"
                  onClickCb={() => {}}
                  disabled={submitDisabled}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    ...(submitDisabled && {
                      backgroundColor: "#E5E7EB",
                      color: "#9CA3AF",
                      cursor: "not-allowed",
                      pointerEvents: "none",
                      border: "none",
                    }),
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
